import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Star, ArrowRight, ShieldCheck, Tag, BadgeCheck, MapPin, Zap, Clock, Sparkles, X, Gift, Trophy, Award } from 'lucide-react';
import { ASSETS } from '../mock/data';
import DesktopHeader from '../components/DesktopHeader';

const Landing = () => {
  const nav = useNavigate();
  const [showRewards, setShowRewards] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <DesktopHeader showLocation={false} />

      {/* HERO SECTION */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #FFF7C2 0%, #FFEB99 100%)' }}>
        {/* Decorative blobs */}
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#FFE066] opacity-70 hidden md:block" />
        <div className="absolute top-40 -left-24 w-80 h-80 rounded-full bg-[#F5E663] opacity-60 hidden md:block" />

        {/* Mobile top bar (only on small screens) */}
        <div className="md:hidden px-5 pt-6 flex items-center justify-between">
          <img src={ASSETS.logo} alt="Clean24" className="h-20 object-contain" />
          <button onClick={() => nav('/login')} className="w-11 h-11 rounded-full bg-white shadow flex items-center justify-center">
            <Lock className="w-4 h-4 text-neutral-700" />
          </button>
        </div>

        <div className="relative max-w-[1280px] mx-auto px-5 md:px-8 py-8 md:py-16 grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* LEFT: Copy + CTA */}
          <div className="order-2 md:order-1">
            <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur border border-white text-[#2E7D32] text-xs font-bold px-3 py-1.5 rounded-full">
              <MapPin className="w-3.5 h-3.5" /> Thrissur · Ernakulam
            </div>
            <h1 className="mt-4 text-4xl md:text-6xl font-extrabold text-neutral-900 leading-[1.05] tracking-tight">
              Sparkling clean, <br className="hidden md:block" /><span className="text-[#5AA02C]">on demand.</span>
            </h1>
            <p className="mt-5 text-base md:text-lg text-neutral-700 max-w-lg">
              Book verified cleaners in Thrissur & Ernakulam. Pay cash or UPI. Live tracking included — your cleaner arrives in as little as 30 minutes.
            </p>

            <div className="mt-8 bg-white rounded-2xl shadow-lg p-4 md:p-5 max-w-lg">
              <button onClick={() => nav('/login')} className="w-full flex items-center gap-4 bg-[#2E7D32] hover:bg-[#276D2A] active:bg-[#20591F] transition-colors text-white rounded-xl px-5 py-4">
                <span className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6"><path d="M3 11l9-8 9 8v10a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1V11z" stroke="white" strokeWidth="2" strokeLinejoin="round"/></svg>
                </span>
                <span className="flex-1 text-left">
                  <span className="block font-semibold text-base md:text-lg">I need Cleaner / Worker</span>
                  <span className="block text-xs md:text-sm opacity-90">Book in 60 seconds</span>
                </span>
                <ArrowRight className="w-5 h-5" />
              </button>
              <p className="text-center text-sm text-neutral-600 mt-3">
                Already have an account? <button onClick={() => nav('/login')} className="text-[#5AA02C] font-semibold hover:underline">Log in</button>
              </p>
            </div>

            <div className="mt-6 flex items-center gap-6 text-sm text-neutral-700">
              <div className="flex items-center gap-1.5">
                <div className="flex -space-x-2">
                  <img src={ASSETS.staff} className="w-8 h-8 rounded-full object-cover border-2 border-white" alt="" />
                  <img src={ASSETS.team} className="w-8 h-8 rounded-full object-cover border-2 border-white" alt="" />
                </div>
                <span className="font-semibold">10,000+ bookings</span>
              </div>
              <div className="flex items-center gap-1">
                <Star className="w-4 h-4 fill-[#F5B301] text-[#F5B301]" />
                <span className="font-semibold">4.9 rating</span>
              </div>
            </div>
          </div>

          {/* RIGHT: Hero image */}
          <div className="order-1 md:order-2 relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img src={ASSETS.waterTank} alt="Water tank cleaning" className="w-full h-64 md:h-[520px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

              {/* Rewards badge - clickable */}
              <button onClick={() => setShowRewards(true)} className="absolute top-4 right-4 flex flex-col items-center hover:scale-105 transition-transform group">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#F59E0B] to-[#D97706] flex items-center justify-center shadow-lg group-hover:shadow-xl">
                  <Star className="w-7 h-7 text-white fill-white" />
                </div>
                <div className="mt-1 text-[10px] font-bold text-[#B45309] bg-white px-2 py-0.5 rounded-full">REWARDS</div>
              </button>

              <div className="absolute bottom-6 left-6 right-6">
                <p className="text-white text-xl md:text-2xl font-bold leading-tight max-w-md">Water tank cleaning · safety-certified crew</p>
                <div className="mt-3 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur text-[#2E7D32] text-xs font-bold px-3 py-1.5 rounded-full">
                  <Zap className="w-3.5 h-3.5 fill-[#2E7D32]" /> 30-Min Express Available
                </div>
              </div>
            </div>

            {/* Floating card - only desktop */}
            <div className="hidden md:flex absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-[#E8F5E9] flex items-center justify-center">
                <Clock className="w-5 h-5 text-[#2E7D32]" />
              </div>
              <div>
                <p className="font-bold text-sm">Live tracking</p>
                <p className="text-xs text-neutral-500">Know when your cleaner arrives</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BADGES SECTION */}
      <section className="max-w-[1280px] mx-auto px-5 md:px-8 py-10 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          <TrustCard icon={<ShieldCheck className="w-7 h-7 text-[#2E7D32]" />} bg="bg-[#E8F5E9]" title="100% Guarantee" text="Not happy? We'll re-clean free or refund — no questions asked." />
          <TrustCard icon={<Tag className="w-7 h-7 text-[#EA580C]" />} bg="bg-[#FFF3E0]" title="Transparent Pricing" text="Flat hourly rates. No hidden fees. Never tip the cleaner extra." />
          <TrustCard icon={<BadgeCheck className="w-7 h-7 text-[#1E88E5]" />} bg="bg-[#E3F2FD]" title="Experienced Cleaners" text="Uniformed pros - trained, background-checked & top-rated." />
        </div>
      </section>

      {/* SERVICES PREVIEW SECTION */}
      <section className="bg-neutral-50 py-12 md:py-20">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-sm font-bold text-[#2E7D32] uppercase tracking-wide">Our Services</p>
            <h2 className="mt-2 text-3xl md:text-5xl font-extrabold text-neutral-900">Everything, one tap away</h2>
            <p className="mt-3 text-neutral-600">From daily home cleaning to specialised water tank & electrician services.</p>
          </div>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { img: ASSETS.sinkClean, title: 'Home Cleaning' },
              { img: ASSETS.wallClean, title: 'Deep Cleaning' },
              { img: ASSETS.toiletClean, title: 'Bathroom Cleaning' },
              { img: ASSETS.waterTank, title: 'Water Tank' },
              { img: ASSETS.fanClean, title: 'Fan Repair' },
              { img: ASSETS.dishClean, title: 'Kitchen Clean' },
              { img: ASSETS.bedChange, title: 'Bed Making' },
              { img: ASSETS.staff, title: 'Verified Staff' },
            ].map((s, i) => (
              <div key={i} onClick={() => nav('/login')} className="relative h-40 md:h-52 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-pointer group">
                <img src={s.img} alt={s.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-3 left-4 right-4">
                  <p className="text-white font-bold">{s.title}</p>
                  <p className="text-white/80 text-xs">from ₹399</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <button onClick={() => nav('/login')} className="inline-flex items-center gap-2 bg-[#2E7D32] hover:bg-[#276D2A] text-white px-6 py-3 rounded-xl font-semibold transition-colors">
              Explore all services <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="py-12 md:py-16" style={{ background: 'linear-gradient(180deg, #FFF7C2 0%, #FFEB99 100%)' }}>
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-neutral-900">Are you a professional cleaner?</h2>
            <p className="mt-3 text-neutral-700 max-w-lg">Join Clean24 as a cleaner and get consistent jobs, timely payouts, and support. Flexible hours — be your own boss.</p>
            <button onClick={() => nav('/cleaner/login')} className="mt-5 bg-[#2E7D32] hover:bg-[#276D2A] text-white px-6 py-3 rounded-xl font-semibold transition-colors inline-flex items-center gap-2">
              Cleaner Login <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="relative">
            <img src={ASSETS.staff} alt="Clean24 staff" className="rounded-2xl shadow-xl w-full h-64 md:h-80 object-cover" />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-neutral-900 text-neutral-300 py-10">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 grid md:grid-cols-4 gap-8">
          <div>
            <img src={ASSETS.logo} alt="Clean24" className="h-12 bg-white rounded-lg p-1 mb-3" />
            <p className="text-sm">Sparkling clean, on demand. Thrissur & Ernakulam.</p>
          </div>
          <FooterCol title="Services" items={[
            { label: 'Home Cleaning', to: '/login' },
            { label: 'Deep Cleaning', to: '/login' },
            { label: 'Water Tank', to: '/login' },
            { label: 'Electrician', to: '/login' },
          ]} nav={nav} />
          <FooterCol title="Company" items={[
            { label: 'About', to: '/about' },
            { label: 'Careers', to: '/about' },
            { label: 'Blog', to: '/about' },
            { label: 'Contact', to: '/support' },
          ]} nav={nav} />
          <FooterCol title="Legal" items={[
            { label: 'Terms', to: '/terms' },
            { label: 'Privacy', to: '/privacy' },
            { label: 'Refunds', to: '/refunds' },
            { label: 'Support', to: '/support' },
          ]} nav={nav} />
        </div>
        <div className="mt-8 pt-6 border-t border-neutral-800 max-w-[1280px] mx-auto px-5 md:px-8">
          {/* Workspace login strip */}
          <div className="mb-5">
            <p className="text-[11px] font-bold uppercase tracking-widest text-neutral-500 mb-2">Workspace login</p>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => nav('/login')} data-testid="workspace-login-customer" className="text-xs font-semibold px-3 py-1.5 rounded-full bg-neutral-800 hover:bg-[#2E7D32] text-neutral-200 hover:text-white border border-neutral-700 transition-colors">Customer</button>
              <button onClick={() => nav('/cleaner/login')} data-testid="workspace-login-cleaner" className="text-xs font-semibold px-3 py-1.5 rounded-full bg-neutral-800 hover:bg-[#EA580C] text-neutral-200 hover:text-white border border-neutral-700 transition-colors">Cleaner</button>
              <button onClick={() => nav('/staff/login')} data-testid="workspace-login-staff" className="text-xs font-semibold px-3 py-1.5 rounded-full bg-neutral-800 hover:bg-[#1E88E5] text-neutral-200 hover:text-white border border-neutral-700 transition-colors">Staff / Supervisor</button>
              <button onClick={() => nav('/franchise/login')} data-testid="workspace-login-franchise" className="text-xs font-semibold px-3 py-1.5 rounded-full bg-neutral-800 hover:bg-[#7B1FA2] text-neutral-200 hover:text-white border border-neutral-700 transition-colors">Franchise Owner</button>
              <button onClick={() => nav('/ho/login')} data-testid="workspace-login-ho" className="text-xs font-semibold px-3 py-1.5 rounded-full bg-neutral-800 hover:bg-[#4338CA] text-neutral-200 hover:text-white border border-neutral-700 transition-colors">Head Office (CA)</button>
              <button onClick={() => nav('/admin/login')} data-testid="workspace-login-admin" className="text-xs font-semibold px-3 py-1.5 rounded-full bg-neutral-800 hover:bg-white text-neutral-200 hover:text-neutral-900 border border-neutral-700 transition-colors">Super Admin</button>
            </div>
          </div>
          <div className="pt-4 border-t border-neutral-800 flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-neutral-500">
            <p>© 2026 Clean24. All rights reserved.</p>
            <p>Thrissur · Ernakulam</p>
          </div>
        </div>
      </footer>

      {/* REWARDS MODAL */}
      {showRewards && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowRewards(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="relative p-6 text-white" style={{ background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' }}>
              <button onClick={() => setShowRewards(false)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
                <X className="w-4 h-4" />
              </button>
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center backdrop-blur">
                <Trophy className="w-8 h-8 text-white" />
              </div>
              <h2 className="mt-3 text-2xl font-extrabold">Clean24 Rewards</h2>
              <p className="text-sm opacity-90">Earn on every booking. Redeem for discounts.</p>
            </div>

            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3 p-3 rounded-xl bg-[#FFF3E0] border border-[#FFE0B2]">
                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                  <Gift className="w-5 h-5 text-[#EA580C]" />
                </div>
                <div>
                  <p className="font-bold text-sm">Welcome bonus</p>
                  <p className="text-xs text-neutral-600">Get ₹100 credits on sign up</p>
                </div>
              </div>

              <div>
                <p className="font-bold text-sm mb-2">How it works</p>
                <ul className="space-y-2">
                  <RewardStep n="1" text="Book any Clean24 service and earn 5% back as Clean24 Coins." />
                  <RewardStep n="2" text="Refer friends and get ₹100 for each successful signup." />
                  <RewardStep n="3" text="Redeem coins at checkout — 1 coin = ₹1 discount." />
                </ul>
              </div>

              <div className="grid grid-cols-3 gap-2 pt-2 border-t border-neutral-100">
                <TierBox icon={Award} color="#94A3B8" bg="#F1F5F9" label="Silver" range="0 – 999" />
                <TierBox icon={Award} color="#F59E0B" bg="#FEF3C7" label="Gold" range="1K – 4.9K" />
                <TierBox icon={Award} color="#7C3AED" bg="#EDE9FE" label="Platinum" range="5K+" />
              </div>

              <button onClick={() => { setShowRewards(false); nav('/login'); }} className="w-full py-3 rounded-xl bg-[#EA580C] hover:bg-[#C2410C] text-white font-semibold text-sm transition-colors">
                Continue as customer & earn ₹100
              </button>
              <p className="text-center text-[11px] text-neutral-500">Terms apply. Rewards valid for 90 days.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const RewardStep = ({ n, text }) => (
  <li className="flex items-start gap-3">
    <span className="w-6 h-6 rounded-full bg-[#2E7D32] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{n}</span>
    <span className="text-sm text-neutral-700">{text}</span>
  </li>
);

const TierBox = ({ icon: Icon, color, bg, label, range }) => (
  <div className="rounded-lg p-3 text-center" style={{ background: bg }}>
    <Icon className="w-5 h-5 mx-auto" style={{ color }} />
    <p className="mt-1 text-xs font-bold" style={{ color }}>{label}</p>
    <p className="text-[10px] text-neutral-600 mt-0.5">{range}</p>
  </div>
);

const TrustCard = ({ icon, bg, title, text }) => (
  <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow border border-neutral-100">
    <div className={`w-14 h-14 rounded-full ${bg} flex items-center justify-center`}>{icon}</div>
    <p className="mt-4 text-lg font-bold text-neutral-900">{title}</p>
    <p className="mt-1 text-sm text-neutral-600">{text}</p>
  </div>
);

const FooterCol = ({ title, items, nav }) => (
  <div>
    <p className="font-bold text-white mb-3 text-sm">{title}</p>
    <ul className="space-y-1.5 text-sm">
      {items.map((i) => (
        <li key={i.label}>
          <button onClick={() => nav(i.to)} className="hover:text-white transition-colors">{i.label}</button>
        </li>
      ))}
    </ul>
  </div>
);

export default Landing;
